import React, { useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { Sun } from 'lucide-react';
import { hasSubclassFeature } from '../../../shared/rules/subclasses/index.mjs';
import { getSpellSlotProfile } from '../../../shared/rules/spellSlots.mjs';
import apiFetch from '../../../utils/apiFetch';
import { notify } from '../../../utils/notification';
import SpellControlsBar from './SpellControlsBar';

const HOLY_NIMBUS = 'paladin:devotion:holy-nimbus';

/**
 * Holy Nimbus (Oath of Devotion 20): a Bonus Action tile that fills the Aura of Protection with holy power for 10
 * minutes. While it lasts the tile asks before ending it (no action), so a stray click can't cancel it. Once the use is
 * spent and a level 5 slot remains, the tile offers to expend that slot to restore the use and start it again. The
 * server spends the once-per-Long-Rest use and the slot, deals the Radiant damage when an enemy's turn starts in the
 * aura, and gives Holy Ward against Fiends and Undead.
 */
export default function HolyNimbusControls({ form, characterId, combatState, isActiveTurn, onCharacterChange, onCombatStateReconciled }) {
  const [busy, setBusy] = useState(false), [confirming, setConfirming] = useState(null);
  const lock = useRef(false);
  const selfId = String(characterId);
  if (!hasSubclassFeature(form, HOLY_NIMBUS)) return null;
  const active = (combatState?.activeEffects || []).some(effect => effect.definitionId === HOLY_NIMBUS && String(effect.targetCombatantId) === selfId);
  const used = Boolean(form?.classState?.paladin?.holyNimbus?.used);
  const slotLeft = used && (getSpellSlotProfile(form).spellcasting.remainingByLevel['5'] || 0) > 0;
  const inCombat = Number.isInteger(combatState?.activeTurn) && Boolean(combatState?.participants?.[combatState.activeTurn]);
  const ledger = combatState?.turnActionLedger?.combatantId === selfId && combatState.turnActionLedger.turnSequence === combatState.turnSequence ? combatState.turnActionLedger : null;
  const available = active || ((!used || slotLeft) && (!inCombat || (isActiveTurn && !ledger?.bonusActionSpent)));
  // A prompt whose condition lapsed (the Nimbus ended elsewhere, the last slot went) no longer blocks the tile.
  const prompt = confirming === 'end' && active ? 'end' : confirming === 'restore' && !active && slotLeft ? 'restore' : null;
  const run = async ({ expendSlot = false } = {}) => {
    if (lock.current) return;
    lock.current = true; setBusy(true);
    try {
      const body = { eventId: window.crypto?.randomUUID?.() || `holy-nimbus:${Date.now()}:${Math.random().toString(36).slice(2)}`,
        ...(active ? { end: true } : { turnSequence: inCombat ? combatState.turnSequence : null, ...(expendSlot ? { expendSlot: true } : {}) }) };
      const response = await apiFetch(`/characters/${encodeURIComponent(String(form?._id || characterId))}/paladin/holy-nimbus`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Holy Nimbus is unavailable.');
      if (result.holyNimbus) onCharacterChange?.(previous => ({ ...previous, ...(result.spellcastingState ? { spellcastingState: result.spellcastingState } : {}),
        classState: { ...previous?.classState, paladin: { ...previous?.classState?.paladin, holyNimbus: result.holyNimbus } } }));
      onCombatStateReconciled?.(result.combat);
      notify(result.ended ? 'Holy Nimbus ended.' : `Holy Nimbus${result.slot ? ' (level 5 slot expended)' : ''} · 10 minutes: enemies starting their turn in your aura take Radiant damage.`, result.ended ? 'info' : 'success');
      setConfirming(null);
    } catch (error) { notify(error.message || 'Holy Nimbus is unavailable.', 'warning'); }
    finally { lock.current = false; setBusy(false); }
  };
  return <>
    <button type="button" data-action-tile="holy-nimbus" className="combat-hud-resource-tile combat-hud-feature-action-tile" style={{ '--hud-resource-accent': '#ffd166' }}
      aria-label={active ? 'Holy Nimbus: active' : 'Holy Nimbus'} disabled={busy || Boolean(prompt) || !available}
      onClick={active ? () => setConfirming('end') : used ? () => setConfirming('restore') : () => run()}
      title={active ? 'Active · end it (no action)' : slotLeft ? 'Spent · expend a level 5 spell slot to use it again' : used ? 'Spent until a Long Rest' : 'Bonus Action · 10 minutes · once per Long Rest'}>
      <span className="combat-hud-resource-tile__icon" aria-hidden="true"><Sun size={20} /></span>
      <span className="combat-hud-resource-tile__name">Holy Nimbus</span>
      <span className="combat-hud-resource-tile__value">{active ? 'Active' : used ? 'Used' : 'Ready'}</span>
    </button>
    {prompt === 'end' && <SpellControlsBar label="Holy Nimbus" hint={`End Holy Nimbus now? ${used ? 'Its use is spent until a Long Rest.' : ''}`.trim()}
      actions={<>
        <Button size="sm" variant="danger" disabled={busy} onClick={() => run()}>End Holy Nimbus</Button>
        <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => setConfirming(null)}>Keep it</Button>
      </>}/>}
    {prompt === 'restore' && <SpellControlsBar label="Holy Nimbus" hint="Holy Nimbus is spent. Expend a level 5 spell slot to restore it and use it now (Bonus Action)?"
      actions={<>
        <Button size="sm" variant="warning" disabled={busy} onClick={() => run({ expendSlot: true })}>Expend a level 5 slot</Button>
        <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => setConfirming(null)}>Cancel</Button>
      </>}/>}
  </>;
}
